import { type PropsWithChildren, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Toaster } from 'sonner'
import { LiquidationAlertOverlay } from '@/components/notifications/LiquidationAlertOverlay'
import { useBreakpoints } from '@/hooks/useBreakpoints'
import { DepositProvider } from '@/providers/DepositProvider'
import { type IRootState, store } from '@/store'
import {
  syncSystemTheme,
  toggleAnimation,
  toggleLayout,
  toggleLocale,
  toggleMenu,
  toggleNavbar,
  toggleRTL,
  toggleTheme,
} from '@/store/themeConfigSlice'
import { TOAST_TIMEOUT } from '@/utils/constants'

function App({ children }: PropsWithChildren) {
  const themeConfig = useSelector((state: IRootState) => state.themeConfig)
  const { isTablet: _isTablet } = useBreakpoints()
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(toggleTheme(localStorage.getItem('theme') || themeConfig.theme))
    dispatch(toggleMenu(localStorage.getItem('menu') || themeConfig.menu))
    dispatch(toggleLayout(localStorage.getItem('layout') || themeConfig.layout))
    dispatch(toggleRTL(localStorage.getItem('rtlClass') || themeConfig.rtlClass))
    dispatch(toggleAnimation(localStorage.getItem('animation') || themeConfig.animation))
    dispatch(toggleNavbar(localStorage.getItem('navbar') || themeConfig.navbar))
    dispatch(toggleLocale(localStorage.getItem('i18nextLng') || themeConfig.locale))
    localStorage.removeItem('semidark')
  }, [
    dispatch,
    themeConfig.theme,
    themeConfig.menu,
    themeConfig.layout,
    themeConfig.rtlClass,
    themeConfig.animation,
    themeConfig.navbar,
    themeConfig.locale,
  ])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleThemeChange = () => dispatch(syncSystemTheme())

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleThemeChange)
      return () => mediaQuery.removeEventListener('change', handleThemeChange)
    }

    mediaQuery.addListener(handleThemeChange)
    return () => mediaQuery.removeListener(handleThemeChange)
  }, [dispatch])

  return (
    <div
      className={`${(store.getState().themeConfig.sidebar && 'toggle-sidebar') || ''} ${themeConfig.menu} ${
        themeConfig.layout
      } ${
        themeConfig.rtlClass
      } main-section antialiased relative font-primary text-sm font-normal flex flex-col h-screen w-full text-black dark:text-white-dark`}
    >
      <DepositProvider>
        {children}
        <LiquidationAlertOverlay />
      </DepositProvider>
      <Toaster
        theme={themeConfig.resolvedTheme}
        duration={TOAST_TIMEOUT}
        visibleToasts={5}
        position={'bottom-left'}
        expand={false}
        gap={12}
        offset={12}
      />
    </div>
  )
}

export default App
